/**
 * Join Requests Sheet Component
 * Admin-only sheet for managing community join requests
 */

import { Check, Loader2, X } from "lucide-react"
import { useState } from "react"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Textarea } from "@/components/ui/textarea"
import { useJoinRequestActions, useJoinRequests } from "../hooks/useCommunityMembership"

// Local helper function to get initials
function getInitials(name: string): string {
  const parts = name.trim().split(" ").filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
  }
  return parts[0]?.substring(0, 2).toUpperCase() || ""
}

interface JoinRequestsSheetProps {
  communityId: string
  trigger?: React.ReactNode
}

export function JoinRequestsSheet({ communityId, trigger }: JoinRequestsSheetProps) {
  const [open, setOpen] = useState(false)
  const [rejectionReason, setRejectionReason] = useState<string>("")
  const [rejectingRequestId, setRejectingRequestId] = useState<string | null>(null)

  const { requests, total, isLoading, refetch } = useJoinRequests(communityId)
  const { acceptRequest, rejectRequest, isAccepting, isRejecting } = useJoinRequestActions()

  const handleAccept = (requestId: string) => {
    acceptRequest(
      { communityId, requestId },
      {
        onSuccess: () => {
          refetch()
        },
        onError: error => {
          // Show error toast/notification
          console.error("Failed to accept request:", error)
        },
      },
    )
  }

  const handleReject = (requestId: string) => {
    if (rejectionReason.trim() === "") {
      // Reject without reason
      rejectRequest(
        { communityId, requestId },
        {
          onSuccess: () => {
            refetch()
            setRejectionReason("")
            setRejectingRequestId(null)
          },
        },
      )
    } else {
      // Reject with reason
      rejectRequest(
        { communityId, requestId, reason: rejectionReason },
        {
          onSuccess: () => {
            refetch()
            setRejectionReason("")
            setRejectingRequestId(null)
          },
        },
      )
    }
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            Join Requests {total > 0 && `(${total})`}
          </Button>
        )}
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Join Requests</SheetTitle>
          <SheetDescription>
            {total === 0
              ? "No pending join requests"
              : `${total} pending ${total === 1 ? "request" : "requests"}`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Check className="w-12 h-12 text-muted-foreground mb-2" />
              <p className="text-muted-foreground">All caught up!</p>
              <p className="text-sm text-muted-foreground">No pending requests to review.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map(request => (
                <div
                  key={request.id}
                  className="flex items-start gap-3 p-3 rounded-lg border bg-card"
                >
                  <Avatar
                    src={request.requesterAvatar}
                    fallback={getInitials(request.requesterName)}
                    size="lg"
                    className="h-10 w-10"
                  />

                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{request.requesterName}</p>
                    <p className="text-xs text-muted-foreground">
                      Requested {new Date(request.requestedAt).toLocaleDateString()}
                    </p>

                    {rejectingRequestId === request.id && (
                      <div className="mt-2">
                        <Textarea
                          placeholder="Optional reason for rejection..."
                          value={rejectionReason}
                          onChange={e => setRejectionReason(e.target.value)}
                          className="min-h-[60px] text-sm"
                          autoFocus
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    {rejectingRequestId === request.id ? (
                      <>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          onClick={() => handleReject(request.id)}
                          disabled={isRejecting}
                        >
                          {isRejecting ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Check className="w-3 h-3" />
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => {
                            setRejectingRequestId(null)
                            setRejectionReason("")
                          }}
                          disabled={isRejecting}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          onClick={() => handleAccept(request.id)}
                          disabled={isAccepting}
                        >
                          {isAccepting ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Check className="w-3 h-3 text-green-600" />
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          onClick={() => setRejectingRequestId(request.id)}
                          disabled={isRejecting}
                        >
                          <X className="w-3 h-3 text-red-600" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {total > 0 && !isLoading && (
            <div className="pt-2 text-center text-xs text-muted-foreground">
              {total} pending {total === 1 ? "request" : "requests"}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
